import Vue from "vue";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import { auth, db, rtdb } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import localforage from "@/utils/localforage/localforage";
import { esEmbebido } from "@/utils/embebido";
import { esSesionSoporte } from "@/utils/sesionSoporte";
import {
  setTrackers,
  clearTrackers,
} from "@/plugins/trackerManager/trackerManager";
import createActivityTrackerFirestore from "./plugins/trackerManager/activityTracker/activityTracker";
import createLocationTrackerFirestore from "./plugins/trackerManager/locationTracker/locationTracker";
import createPresenceTrackerRealtime from "./plugins/trackerManager/presenceTracker/presenceTracker";

Vue.config.productionTip = false;

// Manejo global de ChunkLoadErrors (Pantalla en blanco sin Navbar/Footer)
// Esto ocurre cuando Firebase Hosting elimina un chunk antiguo tras una actualización
// y Vue falla al intentar cargarlo de forma asíncrona dentro de DynamicWrapper.
window.addEventListener('unhandledrejection', function (event) {
  if (
    event.reason &&
    (/Loading( CSS)? chunk .* failed/i.test(event.reason.message) ||
      event.reason.name === "ChunkLoadError")
  ) {
    console.warn("ChunkLoadError detectado globalmente. Forzando recarga para obtener la nueva versión...");
    window.location.reload();
  }
});

let vueApp = null;
let activityTracker = null;
let locationTracker = null;
let presenceTracker = null;

async function initTrackerFor(uid) {
  if (activityTracker) {
    try {
      activityTracker.stop("auth-changed");
    } catch (_) {
      //
    }
    activityTracker = null;
  }

  if (locationTracker) {
    try {
      locationTracker.stop("auth-changed");
    } catch (_) {
      //
    }
    locationTracker = null;
  }

  if (presenceTracker) {
    try {
      await presenceTracker.stop("auth-changed");
    } catch (_) {
      //
    }
    presenceTracker = null;
  }

  const obtenerUsuario = await store.state.obtenerUsuario(uid);
  const idEmpresa =
    obtenerUsuario &&
    obtenerUsuario.preferencias &&
    obtenerUsuario.preferencias.idEmpresaSeleccionada
      ? obtenerUsuario.preferencias.idEmpresaSeleccionada
      : "";

  activityTracker = createActivityTrackerFirestore({
    db,
    idUsuario: uid,
    idEmpresa,
    milisegundosInactividad: 60000,
    coleccion: "riskmann_registro_actividad_usuarios",
    ruta: router.currentRoute.fullPath,
  });

  locationTracker = createLocationTrackerFirestore({
    db,
    idUsuario: uid,
    idEmpresa,
    coleccion: "riskmann_registro_ubicacion_usuarios",
    minUpdateMs: 30000,
    minDistanceMeters: 15,
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 10000,
    debug: false,
    onError(error) {
      console.warn("[locationTracker]", error);
    },
  });

  presenceTracker = createPresenceTrackerRealtime({
    rtdb,
    idUsuario: uid,
    idEmpresa,
    rutaInicial: router.currentRoute.fullPath,
    basePath: "riskmann_registro_estado_usuarios",
    debug: false,
  });

  setTrackers({
    activityTracker: activityTracker,
    locationTracker: locationTracker,
    presenceTracker: presenceTracker,
  });

  Vue.prototype.$activityTracker = activityTracker;
  Vue.prototype.$locationTracker = locationTracker;
  Vue.prototype.$presenceTracker = presenceTracker;
}

export async function stopTrackers(reason = "stop-all") {
  if (activityTracker) {
    try {
      activityTracker.stop(reason);
    } catch (_) {
      //
    }
    activityTracker = null;
  }

  if (locationTracker) {
    try {
      locationTracker.stop(reason);
    } catch (_) {
      //
    }
    locationTracker = null;
  }

  if (presenceTracker) {
    try {
      await presenceTracker.stop(reason);
    } catch (_) {
      //
    }
    presenceTracker = null;
  }

  clearTrackers();
  Vue.prototype.$activityTracker = null;
  Vue.prototype.$locationTracker = null;
  Vue.prototype.$presenceTracker = null;
}

function mountAppOnce() {
  if (vueApp) return;
  vueApp = new Vue({
    router,
    store,
    localforage,
    render: (h) => h(App),
    async mounted() {
      if (this.$activityTracker) this.$activityTracker.start();
      if (this.$locationTracker) this.$locationTracker.start();
      if (this.$presenceTracker) await this.$presenceTracker.start();
    },
    async beforeDestroy() {
      if (this.$activityTracker) this.$activityTracker.stop("app-destroy");
      if (this.$locationTracker) this.$locationTracker.stop("app-destroy");
      if (this.$presenceTracker)
        await this.$presenceTracker.stop("app-destroy");
    },
  }).$mount("#app");

  router.afterEach((to, from) => {
    if (to.fullPath !== from.fullPath) {
      if (Vue.prototype.$activityTracker) {
        Vue.prototype.$activityTracker.switchPage(to.fullPath, "route");
      }
      if (Vue.prototype.$presenceTracker) {
        Vue.prototype.$presenceTracker.switchPage(to.fullPath);
      }
    }
  });
}

onAuthStateChanged(auth, async (user) => {
  // En el iframe de la Capacitación gratuita (modo embebido) ya rastrea la página principal. En una
  // sesión de soporte no se rastrea: la actividad y la ubicación serían las de soporte, no las del usuario.
  if (user && process.env.NODE_ENV === "production" && !esEmbebido() && !esSesionSoporte()) {
    await initTrackerFor(user.uid);
  } else {
    await stopTrackers("logout");
  }
  mountAppOnce();
});
